import { addTruthyParamsToUrl } from '@zapier/url-utils';
import {
  commonErrors as Errors,
  resourceNames,
} from '@zapier/toolbox-redux-framework';
import {
  fetch,
  fetchJson,
  postJson,
  putJson,
} from '@zapier/toolbox-browser-fetch';

import {
  ZAPIER_APP_API_PATH,
  ZAPIER_APP_V1_API_PATH,
  FORM_TYPES,
} from 'app/developer-v3/constants';

import type {
  ObjectType,
  OperationMethodType,
  PartialDefinition,
} from 'app/developer-v3/types';
import type { TestData } from 'app/developer-v3/components/RequestTester';
import { ZapData } from 'app/entities/Zap/types';
import { TaskData } from 'app/developer-v3/types/task';

const Data = {
  getAppFormFields: (formId: FORM_TYPES) => {
    const url = `/api/developer/v2/forms/${formId}`;
    return fetchJson(url);
  },
  saveAppImage: (appId: number, imageFile: File) => {
    const url = `${ZAPIER_APP_API_PATH}/${appId}/image`;

    const formData = new FormData();
    formData.append('image', imageFile);

    return fetch(url, { method: 'POST', body: formData })
      .then(fetch.success)
      .then(fetch.json);
  },
  promoteAppVersion: async (
    appId: number,
    versionNumber: string,
    changelog?: string
  ) => {
    const url = `${ZAPIER_APP_API_PATH}/${appId}/versions/${versionNumber}/promote/production`;
    return putJson(url, {
      changelog,
    });
  },
  deprecateAppVersion: async (
    appId: number,
    versionNumber: string,
    date: string
  ) => {
    const url = `${ZAPIER_APP_API_PATH}/${appId}/versions/${versionNumber}/deprecate`;
    return putJson(url, {
      deprecation_date: date,
    });
  },
  migrateAppVersion: async (
    appId: number,
    fromVersionNumber: string,
    toVersionNumber: string,
    options: { percentage?: number; email?: string }
  ) => {
    const url = `${ZAPIER_APP_API_PATH}/${appId}/versions/${fromVersionNumber}/migrate-to/${toVersionNumber}`;
    const { percentage, email } = options;

    return postJson(url, { percent: percentage, user: email });
  },
  cloneAppVersion: async (
    appId: number,
    fromVersionNumber: string,
    toVersionNumber: string
  ) => {
    const url = `${ZAPIER_APP_API_PATH}/${appId}/versions/${fromVersionNumber}/clone-to/${toVersionNumber}`;
    return postJson(url, { is_ui: true });
  },
  executeTestRequest: (
    appId: number,
    versionNumber: string,
    objectType: ObjectType,
    objectKey: string,
    partialDefinition: PartialDefinition,
    authenticationId: string,
    operationMethod: OperationMethodType,
    testData: TestData
  ) => {
    const url = `${ZAPIER_APP_API_PATH}/${appId}/versions/${versionNumber}/test`;
    return postJson(url, {
      objectType,
      objectKey,
      partialDefinition,
      authenticationId,
      operationMethod,
      testData,
    });
  },
  batchUpdateEnvironment: (appId: number, appVersion: string, env: Object) => {
    const url = `${ZAPIER_APP_API_PATH}/${appId}/versions/${appVersion}/multi-environment`;
    return postJson(url, env);
  },
  loadTasks: async (queryParams: {}): Promise<TaskData[]> => {
    const url = addTruthyParamsToUrl('/api/zap-history/v2/runs', queryParams);
    try {
      const response = await fetchJson(url);
      return response.objects ?? [];
    } catch (error) {
      throw new Errors.DataGetError({
        resource: resourceNames.tasks,
        response: error,
      });
    }
  },
  loadZaps: async (
    queryParams?: Record<string, string | Number>
  ): Promise<ZapData[]> => {
    const url = addTruthyParamsToUrl('/api/v3/zaps', queryParams);
    try {
      const response = await fetchJson(url);
      return response.objects ?? [];
    } catch (error) {
      throw new Errors.DataGetError({
        resource: resourceNames.zap,
        response: error,
      });
    }
  },
  loadAppEvents: (appId: string | number, filters: {}) => {
    const apiUrl = `${ZAPIER_APP_V1_API_PATH}/apps/${appId}/events`;

    const url = fetch.url(apiUrl, filters);
    return fetchJson(url)
      .then(results => {
        return results?.events || [];
      })
      .catch(response => {
        throw new Errors.DataGetError({
          resource: resourceNames.devAppEvents,
          response,
        });
      });
  },
};

export default Data;
